import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Link, useParams } from "react-router-dom";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import api from "../utils/axios";

const PAGE_SIZE = 25;

function signingAppOrigin() {
  const fromEnv = (process.env.REACT_APP_FRONTEND_URL || "").replace(
    /\/+$/,
    "",
  );
  if (fromEnv) return fromEnv;
  return window.location.origin;
}

const OnDemandSignPortal = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState({
    containerName: "",
    recipientTotal: 0,
    matchedTotal: 0,
    page: 1,
    totalPages: 1,
    limit: PAGE_SIZE,
  });
  const [recipients, setRecipients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  const hasLoadedOnceRef = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useLayoutEffect(() => {
    setPage(1);
  }, [debouncedQ]);

  useEffect(() => {
    setSelected(null);
  }, [debouncedQ]);

  const fetchPage = useCallback(async () => {
    if (!token) {
      setError("Invalid link");
      setLoading(false);
      return;
    }
    try {
      if (hasLoadedOnceRef.current) setListLoading(true);
      const { data: body } = await api.get(
        `/public/ondemand-signing/containers/${encodeURIComponent(token)}`,
        {
          params: {
            page,
            limit: PAGE_SIZE,
            ...(debouncedQ ? { q: debouncedQ } : {}),
          },
        },
      );
      setError(null);
      setMeta({
        containerName: body.containerName || "",
        recipientTotal: body.recipientTotal ?? body.recipientCount ?? 0,
        matchedTotal: body.matchedTotal ?? body.recipientTotal ?? 0,
        page: body.page ?? 1,
        totalPages: body.totalPages ?? 1,
        limit: body.limit ?? PAGE_SIZE,
      });
      setRecipients(Array.isArray(body.recipients) ? body.recipients : []);
      if (body.page != null) setPage(body.page);
      hasLoadedOnceRef.current = true;
    } catch (e) {
      setError(
        e.response?.data?.message || e.message || "Could not load container",
      );
      setRecipients([]);
    } finally {
      setLoading(false);
      setListLoading(false);
    }
  }, [token, page, debouncedQ]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  useEffect(() => {
    if (!selected) return;
    const onPage = recipients.find(
      (r) =>
        r.rowIndex === selected.rowIndex && r.signPath === selected.signPath,
    );
    if (onPage?.signingComplete) setSelected(null);
  }, [recipients, selected]);

  const rowSigningComplete = (r) => r.signingComplete === true;

  const goSign = () => {
    if (!selected?.signPath) return;
    const path = selected.signPath;
    const base = signingAppOrigin();
    window.location.href = `${base}${path.startsWith("/") ? path : `/${path}`}`;
  };

  const isRowSelected = (r) =>
    selected != null &&
    selected.rowIndex === r.rowIndex &&
    selected.signPath === r.signPath;

  const startRow =
    meta.matchedTotal === 0 ? 0 : (meta.page - 1) * meta.limit + 1;
  const endRow = Math.min(meta.page * meta.limit, meta.matchedTotal);

  if (loading && recipients.length === 0 && !error) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <BrandedHeader />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary border-t-transparent mx-auto mb-4" />
            <p className="text-gray-600 text-sm font-medium">Loading…</p>
          </div>
        </div>
        <BrandedFooter />
      </div>
    );
  }

  if (error && !meta.containerName) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <BrandedHeader />
        <div className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="max-w-md w-full bg-white p-8 text-center">
            <h1 className="text-xl font-semibold text-gray-900 mb-2 font-dm-sans">
              Link not available
            </h1>
            <p className="text-gray-600 text-sm">{error}</p>
          </div>
        </div>
        <BrandedFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <BrandedHeader />

      <main className="flex-1 w-full max-w-3xl mx-auto px-3 sm:px-4 py-6 sm:py-10 flex flex-col min-h-0">
        <div className="bg-white flex flex-col flex-1 min-h-[min(100vh-220px,820px)] max-h-[min(100vh-180px,860px)]">
          <div className="flex-shrink-0 p-5 sm:p-6 border-b border-gray-100">
            <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-1">
              On-demand signing
            </p>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 font-dm-sans leading-tight">
              {meta.containerName}
            </h1>
            <p className="text-gray-600 text-sm mt-2">
              Search by name or email, select your row, then continue to your
              personal signing page.
            </p>
            <p className="text-xs text-gray-500 mt-2 tabular-nums">
              {debouncedQ
                ? `${meta.matchedTotal} match${meta.matchedTotal === 1 ? "" : "es"} · ${meta.recipientTotal} signer${meta.recipientTotal === 1 ? "" : "s"} total`
                : `${meta.recipientTotal} signer${meta.recipientTotal === 1 ? "" : "s"}`}
              {meta.matchedTotal > 0 && ` · rows ${startRow}–${endRow}`}
            </p>

            <div className="relative mt-4">
              <MagnifyingGlassIcon
                className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                aria-hidden
              />
              <input
                type="search"
                autoComplete="off"
                placeholder="Search by name or email…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full rounded-md border border-gray-200 bg-white py-2.5 pl-10 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none"
                aria-label="Filter signers by name, email, unit, or department"
              />
            </div>
          </div>

          <div className="flex-1 min-h-0 flex flex-col px-3 sm:px-5 pb-2 relative">
            {listLoading && (
              <div className="absolute inset-0 z-10 bg-white/60 flex items-center justify-center rounded-lg">
                <div className="animate-spin rounded-full h-9 w-9 border-2 border-primary border-t-transparent" />
              </div>
            )}
            <div
              className="flex-1 min-h-[180px] overflow-y-auto overscroll-contain mt-3 sm:mt-4 border border-gray-100 bg-white"
              role="listbox"
              aria-label="Signers"
            >
              {meta.matchedTotal === 0 ? (
                <div className="flex items-center justify-center py-16 px-4 text-center text-sm text-gray-500">
                  {debouncedQ
                    ? "No signers match your search. Try a different term."
                    : "No signers in this container."}
                </div>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {recipients.map((r) => {
                    const done = rowSigningComplete(r);
                    return (
                      <li
                        key={`${r.rowIndex}-${r.email}`}
                        role="option"
                        aria-disabled={done}
                      >
                        <label
                          className={`flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2.5 text-left transition-colors ${
                            done
                              ? "cursor-not-allowed opacity-60 bg-gray-50/80"
                              : `cursor-pointer hover:bg-gray-50 ${
                                  isRowSelected(r) ? "bg-gray-50" : ""
                                }`
                          }`}
                        >
                          <input
                            type="radio"
                            name="recipient"
                            disabled={done}
                            className="h-4 w-4 shrink-0 text-primary border-gray-300 focus:ring-primary disabled:cursor-not-allowed"
                            checked={isRowSelected(r)}
                            onChange={() =>
                              setSelected({
                                rowIndex: r.rowIndex,
                                signPath: r.signPath,
                                name: r.name,
                                email: r.email,
                              })
                            }
                          />
                          <span className="hidden w-9 shrink-0 text-right text-xs font-semibold text-primary/80 tabular-nums sm:inline-block">
                            {r.rowIndex + 1}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-gray-900">
                              {r.name || "Signer"}
                            </span>
                            <span className="block truncate text-xs sm:text-sm text-gray-500">
                              {r.email}
                            </span>
                            {(r.siNo || r.unit || r.department) && (
                              <span className="block truncate text-xs text-gray-400 mt-0.5">
                                {[
                                  r.siNo ? `SI.No ${r.siNo}` : null,
                                  r.unit ? `Unit: ${r.unit}` : null,
                                  r.department ? `Dept: ${r.department}` : null,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </span>
                            )}
                          </span>
                          {done ? (
                            <span className="shrink-0 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-800">
                              Signed
                            </span>
                          ) : null}
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {meta.totalPages > 1 && (
              <div className="flex flex-wrap items-center justify-between gap-2 mt-3 text-sm text-gray-600">
                <button
                  type="button"
                  disabled={meta.page <= 1 || listLoading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-45 disabled:pointer-events-none text-gray-800"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                  Previous
                </button>
                <span className="tabular-nums text-xs sm:text-sm">
                  Page {meta.page} of {meta.totalPages}
                </span>
                <button
                  type="button"
                  disabled={meta.page >= meta.totalPages || listLoading}
                  onClick={() =>
                    setPage((p) => Math.min(meta.totalPages, p + 1))
                  }
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-45 disabled:pointer-events-none text-gray-800"
                >
                  Next
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          <div className="flex-shrink-0 p-4 sm:p-6 pt-2 border-t border-gray-100 bg-white">
            {selected && (
              <p className="text-xs text-gray-600 mb-3 truncate sm:text-center">
                Selected:{" "}
                <span className="font-semibold text-gray-900">
                  {selected.name || "Signer"}
                </span>
                <span className="text-gray-400"> · </span>
                <span className="text-gray-600">{selected.email}</span>
              </p>
            )}
            <button
              type="button"
              disabled={!selected?.signPath || listLoading}
              onClick={goSign}
              className="w-full py-3 rounded-md bg-primary text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-95"
            >
              Continue to sign
            </button>
          </div>
        </div>
      </main>

      <BrandedFooter />
    </div>
  );
};

function BrandedHeader() {
  const logoSrc = `${process.env.PUBLIC_URL || ""}/official-logo.svg`;
  return (
    <header className="bg-white border-b border-gray-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-3"
          aria-label="EzSignly home"
        >
          <img
            src={logoSrc}
            alt="EzSignly"
            className="h-7 w-auto sm:h-8"
            width={136}
            height={34}
          />
        </Link>
        <Link
          to="/login"
          className="text-sm text-gray-600 hover:text-gray-900"
        >
          Log in
        </Link>
      </div>
    </header>
  );
}

function BrandedFooter() {
  const logoSrc = `${process.env.PUBLIC_URL || ""}/official-logo.svg`;
  return (
    <footer className="mt-auto bg-white border-t border-gray-200 py-4 px-4">
      <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 text-xs sm:text-sm text-gray-500 text-center">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <img
            src={logoSrc}
            alt=""
            className="h-5 w-auto opacity-80"
            width={102}
            height={26}
          />
          <span>EzSignly · electronic signatures</span>
        </Link>
      </div>
    </footer>
  );
}

export default OnDemandSignPortal;
